import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Icon } from "@/components/ui/Icon";
import { ROUTES } from "@/constants/routes";
import { useCancelTonUsdtInvoice, useTonUsdtInvoices } from "@/hooks/useTonUsdtInvoiceStatus";

import type { TonUsdtInvoice } from "@/types/tonUsdtPayment";

const invoiceStatusView: Record<
  TonUsdtInvoice["status"],
  { label: string; className: string; description: string }
> = {
  pending: {
    label: "Ожидается",
    className: "bg-primary-500/15 text-primary-400",
    description: "Реквизиты ещё действуют.",
  },
  detected: {
    label: "Перевод найден",
    className: "bg-primary-500/15 text-primary-400",
    description: "Проверяем перевод в сети TON.",
  },
  confirming: {
    label: "Подтверждаем",
    className: "bg-primary-500/15 text-primary-400",
    description: "Проверяем перевод в сети TON.",
  },
  confirmed: {
    label: "Зачислено",
    className: "bg-success/15 text-success",
    description: "Топливо начислено автоматически.",
  },
  expired: {
    label: "Истёк",
    className: "bg-neutral-50/10 text-neutral-300",
    description: "Эти реквизиты больше не принимают оплату.",
  },
  cancelled: {
    label: "Реквизиты отменены",
    className: "bg-neutral-50/10 text-neutral-300",
    description: "Отменены только реквизиты. Уже отправленный перевод в сети TON не отменяется.",
  },
  manual_review: {
    label: "Нужна проверка",
    className: "bg-warning/15 text-warning",
    description: "Обратитесь в поддержку и укажите код платежа.",
  },
  rejected: {
    label: "Нужна проверка",
    className: "bg-warning/15 text-warning",
    description: "Обратитесь в поддержку и укажите код платежа.",
  },
};

const isOpenInvoice = (invoice: TonUsdtInvoice) =>
  invoice.status === "pending" || invoice.status === "detected" || invoice.status === "confirming";

const formatRemainingTime = (remainingSeconds: number) => {
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const TopUpsPage = () => {
  const {
    data: invoicePages,
    isError,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTonUsdtInvoices();
  const { cancelInvoice, isCancellingInvoice } = useCancelTonUsdtInvoice();
  const [invoiceToCancel, setInvoiceToCancel] = useState<TonUsdtInvoice | null>(null);
  const infiniteScrollSentinel = useRef<HTMLDivElement>(null);
  const invoices = invoicePages?.pages.flatMap((page) => page.items) ?? [];
  const hasPendingInvoices = invoicePages?.pages.some((page) =>
    page.items.some((invoice) => invoice.status === "pending")
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const sentinel = infiniteScrollSentinel.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!hasPendingInvoices) return;

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [hasPendingInvoices]);

  const handleCancelInvoice = async () => {
    if (!invoiceToCancel) return;

    try {
      await cancelInvoice(invoiceToCancel.id);
      toast.success(
        "Реквизиты отменены. Новый перевод по этому коду не будет зачислен автоматически."
      );
      setInvoiceToCancel(null);
    } catch {
      toast.error("Не удалось отменить реквизиты. Обновите страницу и попробуйте ещё раз.");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-50">
      <Header variant="profile" />

      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Пополнения</h1>
            <p className="mt-2 text-neutral-300">История реквизитов и статус зачисления топлива.</p>
          </div>
          <Link to={ROUTES.buyFuel}>
            <Button
              variant="secondary"
              icon={<Icon name="plus" />}
            >
              Пополнить топливо
            </Button>
          </Link>
        </div>

        {isLoading && (
          <div className="flex justify-center py-16">
            <Icon
              name="spinner"
              className="size-6 animate-spin text-neutral-400"
            />
          </div>
        )}

        {isError && (
          <div className="rounded-mds border-danger/30 bg-danger/10 border p-5 text-neutral-100">
            Не удалось загрузить историю пополнений. Попробуйте обновить страницу.
          </div>
        )}

        {!isLoading && !isError && invoices.length === 0 && (
          <div className="rounded-mds border border-neutral-800 bg-neutral-900/40 p-8 text-center">
            <Icon
              name="flash"
              className="mx-auto mb-3 size-7 text-neutral-400"
            />
            <p className="font-semibold">Пополнений пока нет</p>
            <p className="mt-2 text-sm text-neutral-400">
              Создайте первые реквизиты для перевода USDT.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {invoices.map((invoice) => {
            const remainingSeconds =
              invoice.status === "pending"
                ? Math.max(0, Math.ceil((Date.parse(invoice.expires_at) - now) / 1000))
                : null;
            const hasExpiredInBrowser = remainingSeconds === 0;
            const status = invoiceStatusView[hasExpiredInBrowser ? "expired" : invoice.status];
            const invoiceIsOpen = isOpenInvoice(invoice) && !hasExpiredInBrowser;

            return (
              <article
                key={invoice.id}
                className="rounded-mds border border-neutral-800 bg-neutral-900/40 p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold">{invoice.amount_usdt} USDT</span>
                      <span className="text-neutral-400">→ {invoice.credited_fuel} топлива</span>
                    </div>
                    <p className="mt-2 text-sm text-neutral-400">
                      Код платежа: {invoice.reference}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${status.className}`}
                  >
                    {status.label}
                  </span>
                </div>

                <p className="mt-4 text-sm text-neutral-300">{status.description}</p>
                {remainingSeconds !== null && !hasExpiredInBrowser && (
                  <p className="mt-2 text-sm font-semibold text-primary-400">
                    Действуют ещё {formatRemainingTime(remainingSeconds)}
                  </p>
                )}

                <div className="mt-5 flex flex-wrap gap-3">
                  {invoiceIsOpen && (
                    <Link to={`${ROUTES.buyFuel}?invoice=${invoice.id}`}>
                      <Button variant="neutral">Открыть реквизиты</Button>
                    </Link>
                  )}
                  {invoice.status === "pending" && !hasExpiredInBrowser && (
                    <Button
                      variant="destructive"
                      onClick={() => setInvoiceToCancel(invoice)}
                    >
                      Отменить реквизиты
                    </Button>
                  )}
                  {(invoice.status === "manual_review" || invoice.status === "rejected") && (
                    <Link to={ROUTES.help}>
                      <Button variant="neutral">Поддержка</Button>
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div
          ref={infiniteScrollSentinel}
          data-testid="invoices-infinite-scroll-sentinel"
          className="flex justify-center py-6"
        >
          {isFetchingNextPage && <Icon name="spinner" className="size-5 animate-spin text-neutral-400" />}
          {hasNextPage && !isFetchingNextPage && (
            <Button variant="neutral" onClick={() => void fetchNextPage()}>
              Загрузить ещё
            </Button>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={invoiceToCancel !== null}
        onOpenChange={(open) => {
          if (!open) setInvoiceToCancel(null);
        }}
        title="Отменить реквизиты?"
        description={
          <>
            Это отменит только реквизиты для автоматического зачисления. Это не отменит уже
            отправленный перевод в сети TON. Если вы уже отправили USDT, не отменяйте реквизиты.
          </>
        }
        isLoading={isCancellingInvoice}
        cancelButton={{ children: "Не отменять", variant: "neutral" }}
        confirmButton={{
          children: "Отменить реквизиты",
          variant: "destructive",
          onClick: () => void handleCancelInvoice(),
        }}
      />
    </div>
  );
};
